import { useState, useEffect } from 'react'
import { useSearchParams, Navigate } from 'react-router-dom'
import BrandColorsPanel from '../../components/BrandColorsPanel'
import { useAdminBusiness } from '../../contexts/AdminBusinessContext'
import { settingsAPI, appSettingsAPI, backupAPI, authAPI, themesAPI } from '../../services/api'
import { useTheme } from '../../contexts/ThemeContext'
import TemplatePicker from '../../components/TemplatePicker'
import { STORE_TEMPLATES, BOOKING_TEMPLATES } from '../../pages/customer/templates/templateMeta'
import { 
  CogIcon, 
  PaintBrushIcon, 
  CloudIcon, 
  ComputerDesktopIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  BuildingOfficeIcon,
  PhotoIcon,
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  TrashIcon,
  CircleStackIcon,
  CreditCardIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import RestaurantBillingPanel from '../../components/RestaurantBillingPanel'
import PaymentMethodsPanel from '../../components/PaymentMethodsPanel'
import SocialLinksFields from '../../components/SocialLinksFields'
import LogoUploadField from '../../components/LogoUploadField'
import { t, locale } from '../../i18n'

const CURRENCY_OPTIONS = [['OMR','Omani Rial'],['SAR','Saudi Riyal'],['AED','UAE Dirham'],['QAR','Qatari Riyal'],['KWD','Kuwaiti Dinar'],['BHD','Bahraini Dinar'],['MAD','Moroccan Dirham'],['USD','US Dollar'],['EUR','Euro'],['GBP','British Pound'],['CAD','Canadian Dollar'],['AUD','Australian Dollar'],['CHF','Swiss Franc'],['JPY','Japanese Yen'],['CNY','Chinese Yuan'],['AED','UAE Dirham'],['SAR','Saudi Riyal'],['QAR','Qatari Riyal'],['TRY','Turkish Lira'],['EGP','Egyptian Pound'],['SEK','Swedish Krona'],['NOK','Norwegian Krone'],['DKK','Danish Krone'],['INR','Indian Rupee']]

function SettingsPage() {
  const { settings, updateSettings, getSetting, loading } = useTheme()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'general')
  const [saving, setSaving] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [localSettings, setLocalSettings] = useState({})
  const [backups, setBackups] = useState([])
  const [loadingBackups, setLoadingBackups] = useState(false)
  const [creatingBackup, setCreatingBackup] = useState(false)
  const { businessType, branch } = useAdminBusiness()
  const [uploadedTheme, setUploadedTheme] = useState(null)
  const [themeFile, setThemeFile] = useState(null)
  const [themeBusy, setThemeBusy] = useState(false)


  useEffect(() => {
    themesAPI.current().then(({ data }) => setUploadedTheme(data.theme)).catch(() => {})
  }, [])

  const uploadTheme = async () => {
    if (!themeFile) return toast.error(t("Choose a .json or .theme file first"))
    setThemeBusy(true)
    try {
      const body = new FormData(); body.append('theme', themeFile)
      const { data } = await themesAPI.upload(body)
      setUploadedTheme(data.theme); setThemeFile(null); toast.success(t("Theme uploaded and saved"))
    } catch (error) { toast.error(t(error.response?.data?.error || 'Theme upload failed')) }
    finally { setThemeBusy(false) }
  }

  const activateTheme = async (active) => {
    setThemeBusy(true)
    try { const { data } = await themesAPI.activate(active); setUploadedTheme(data.theme); toast.success(active ? t("Uploaded theme is live") : t("Uploaded theme paused")) }
    catch (error) { toast.error(t(error.response?.data?.error || 'Could not update theme')) }
    finally { setThemeBusy(false) }
  }

  useEffect(() => {
    // Initialize local settings with current theme settings
    if (settings && Object.keys(settings).length > 0) {
      const initialSettings = {}
      Object.keys(settings).forEach(key => {
        initialSettings[key] = settings[key].value
      })
      console.log('Initializing local settings:', initialSettings)
      setLocalSettings(initialSettings)
    }
  }, [settings])

  const handleSettingChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handleSaveSettings = async () => {
    try {
      setSaving(true)
      
      const result = await updateSettings(localSettings)
      if (result?.success === false) {
        toast.error(t(result.error || 'Failed to save settings'))
      } else if (result) {
        if (result.conversion) toast.success(t("Prices converted {from} → {to} at {v2}. Existing orders were kept unchanged.", { from: result.conversion.from, to: result.conversion.to, v2: Number(result.conversion.rate).toFixed(6) }), { duration: 7000 })
        else toast.success(t("Settings saved successfully!"))
      } else {
        toast.error(t("Failed to save settings"))
      }
    } catch (error) {
      console.error('Failed to save settings:', error)
      toast.error(t("Failed to save settings"))
    } finally {
      setSaving(false)
    }
  }

  const testDatabaseConnection = async () => {
    try {
      setSaving(true)
      const response = await settingsAPI.testDatabaseConnection({
        type: localSettings.dbType || 'sqlite3',
        host: localSettings.dbHost || 'localhost',
        port: localSettings.dbPort || 3306,
        name: localSettings.dbName || 'posq',
        user: localSettings.dbUser || 'posq',
        password: localSettings.dbPassword || '',
        filename: localSettings.dbPath || './data/posq.db'
      })
      
      if (response.data.success) {
        toast.success(t(response.data.message || 'Database connection successful!'))
      } else {
        toast.error(t(response.data.error || 'Database connection failed'))
      }
    } catch (error) {
      console.error('Database connection test failed:', error)
      toast.error(t(error.response?.data?.error || 'Database connection test failed'))
    } finally {
      setSaving(false)
    }
  }

  const initializeDatabase = async () => {
    try {
      setSaving(true)
      const response = await settingsAPI.initializeDatabase({
        type: localSettings.dbType || 'sqlite3',
        host: localSettings.dbHost || 'localhost',
        port: localSettings.dbPort || 3306,
        name: localSettings.dbName || 'posq',
        user: localSettings.dbUser || 'posq',
        password: localSettings.dbPassword || '',
        filename: localSettings.dbPath || './data/posq.db'
      })
      
      if (response.data.success) {
        toast.success(t(response.data.message || 'Database initialized successfully!'))
      } else {
        toast.error(t(response.data.error || 'Database initialization failed'))
      }
    } catch (error) {
      console.error('Database initialization failed:', error)
      toast.error(t(error.response?.data?.error || 'Database initialization failed'))
    } finally {
      setSaving(false)
    }
  }

  const exportDatabase = async () => {
    try {
      setSaving(true)
      const response = await settingsAPI.exportDatabase()
      
      // Create blob and download
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition']
      let filename = `posq-backup-${Date.now()}.db`
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      toast.success(t("Database exported successfully!"))
    } catch (error) {
      console.error('Database export failed:', error)
      toast.error(t("Database export failed"))
    } finally {
      setSaving(false)
    }
  }

  const switchOperatingMode = async (mode) => {
    try {
      setSaving(true)
      const response = await settingsAPI.changeOperatingMode(mode)
      
      if (response.data.success) {
        handleSettingChange('operatingMode', mode)
        
        // Automatically set the correct database type based on mode
        if (mode === 'LOCAL') {
          handleSettingChange('dbType', 'sqlite3')
        } else if (mode === 'CLOUD') {
          handleSettingChange('dbType', 'mysql2')
        }
        
        toast.success(t("Operating mode changed to {mode}", { mode: mode }))
      } else {
        toast.error(t("Failed to change operating mode"))
      }
    } catch (error) {
      console.error('Operating mode change failed:', error)
      toast.error(t("Failed to change operating mode"))
    } finally {
      setSaving(false)
    }
  }

  const loadBackups = async () => {
    try {
      setLoadingBackups(true)
      const response = await backupAPI.listBackups()
      if (response.data.success) {
        setBackups(response.data.backups || [])
      }
    } catch (error) {
      console.error('Failed to load backups:', error)
      toast.error(t("Failed to load backups"))
    } finally {
      setLoadingBackups(false)
    }
  }

  const createBackup = async () => {
    try {
      setCreatingBackup(true)
      const response = await backupAPI.createBackup()
      if (response.data.success) {
        toast.success(t("Backup created successfully!"))
        await loadBackups()
      } else {
        toast.error(t("Failed to create backup"))
      }
    } catch (error) {
      console.error('Failed to create backup:', error)
      toast.error(t("Failed to create backup"))
    } finally {
      setCreatingBackup(false)
    }
  }

  const restoreBackup = async (filename) => {
    if (!confirm(t("Are you sure you want to restore from {filename}? This will replace all current data!", { filename: filename }))) {
      return
    }

    try {
      setSaving(true)
      const response = await backupAPI.restoreBackup(filename)
      if (response.data.success) {
        toast.success(t("Backup restored successfully! Please refresh the page."))
      } else {
        toast.error(t("Failed to restore backup"))
      }
    } catch (error) {
      console.error('Failed to restore backup:', error)
      toast.error(t("Failed to restore backup"))
    } finally {
      setSaving(false)
    }
  }

  const deleteBackup = async (filename) => {
    if (!confirm(t("Are you sure you want to delete {filename}?", { filename: filename }))) {
      return
    }

    try {
      const response = await backupAPI.deleteBackup(filename)
      if (response.data.success) {
        toast.success(t("Backup deleted successfully!"))
        await loadBackups()
      } else {
        toast.error(t("Failed to delete backup"))
      }
    } catch (error) {
      console.error('Failed to delete backup:', error)
      toast.error(t("Failed to delete backup"))
    }
  }

  useEffect(() => {
    if (activeTab === 'backup') {
      loadBackups()
    }
  }, [activeTab])

  const BOOKING_TYPES = ['appointments', 'hotel', 'office']
  const designTab = businessType === 'restaurant'
    ? { id: 'menu_templates', name: 'Menu Templates', icon: PaintBrushIcon }
    : businessType === 'ecommerce'
      ? { id: 'store_templates', name: 'Store Templates', icon: PaintBrushIcon }
      : { id: 'booking_templates', name: 'Booking Page', icon: PaintBrushIcon }

  const tabs = [
    { id: 'general', name: 'General', icon: CogIcon },
    { id: 'branding', name: 'Branding', icon: BuildingOfficeIcon },
    { id: 'theme', name: 'Brand colors', icon: PaintBrushIcon },
    { id: 'theme_upload', name: 'Upload Theme', icon: ArrowUpTrayIcon },
    { id: 'layout', name: 'Layout', icon: AdjustmentsHorizontalIcon },
    { id: 'ui_text', name: 'UI Text', icon: PhotoIcon },
    designTab,
    { id: 'billing', name: 'Billing', icon: CreditCardIcon },
    { id: 'payment', name: 'Payments', icon: BanknotesIcon },
    { id: 'database', name: 'Database', icon: CloudIcon },
    { id: 'backup', name: 'Backup', icon: ArrowDownTrayIcon },
    { id: 'printer', name: 'Printer', icon: ComputerDesktopIcon }
  ].filter(tab => !(['layout', 'ui_text', 'printer'].includes(tab.id) && businessType !== 'restaurant'))

  if (activeTab === 'theme_upload') return <Navigate to="/admin/website" replace />
  if (loading && Object.keys(settings).length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-gray-600">{t("Loading settings...")}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t("Settings")}</h1>
          <p className="text-gray-600 mt-2">{t("Configure your business")}</p>
        </div>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="btn-primary"
        >
          {saving ? (
            <>
              <div className="loading-spinner me-2"></div>
              {t("Saving...")}
            </>
          ) : (
            <>
              <CheckIcon className="h-5 w-5 me-2" />
              {t("Save Settings")}
            </>
          )}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-5 w-5 inline me-2" />
              {t(tab.name)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* General Settings */}
        {activeTab === 'general' && (
  <div className="card">
    <div className="card-header">
      <h2 className="text-xl font-semibold text-gray-900">{t("General Settings")}</h2>
      <p className="text-gray-600">{t("Basic application configuration")}</p>
    </div>
    <div className="card-body space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="form-label">{t("Application Name")}</label>
          <input
            type="text"
            value={localSettings.app_name || ''}
            onChange={(e) => handleSettingChange('app_name', e.target.value)}
            className="form-input"
            placeholder={t("Enter application name")}
          />
        </div>
        <div>
          <label className="form-label">{t("Currency")}</label>
          <select
            value={localSettings.currency || 'OMR'}
            onChange={(e) => handleSettingChange('currency', e.target.value)}
            className="form-input"
          >
            {CURRENCY_OPTIONS.map(([code, name]) => <option key={code} value={code}>{code} ({t(name)})</option>)}
          </select>
          <p className="text-xs text-gray-500 mt-2">{t("Changing currency converts current menu, product and service prices using a live exchange rate. Past orders stay in their original currency.")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="form-label" htmlFor="store-language">{t("Website language")}</label>
          <select id="store-language" value={localSettings.store_language || 'en'} onChange={(e) => handleSettingChange('store_language', e.target.value)} className="form-input">
            <option value="en">{t("English")}</option>
            <option value="ar">{t("العربية (Arabic)")}</option>
            <option value="both">{t("English + العربية (customers choose)")}</option>
          </select>
          <p className="text-xs text-gray-500 mt-2">{t("The language of your public website, menu, cart and checkout. Arabic shows the site right-to-left.")}</p>
        </div>
        <div className="space-y-4">
          {businessType === 'restaurant' && (
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600" checked={localSettings.reservations_enabled !== false}
                onChange={(e) => handleSettingChange('reservations_enabled', e.target.checked)} />
              <span><span className="block font-medium text-gray-900">{t("Accept table reservations online")}</span>
                <span className="block text-xs text-gray-500">{t("When off, the \"Reserve a table\" button is hidden from your website.")}</span></span>
            </label>
          )}
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600" checked={localSettings.low_stock_alerts !== false}
              onChange={(e) => handleSettingChange('low_stock_alerts', e.target.checked)} />
            <span><span className="block font-medium text-gray-900">{t("Email me about low stock")}</span>
              <span className="block text-xs text-gray-500">{t("Sent to the business email below when an item reaches its minimum stock.")}</span></span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="form-label">{t("Tax Rate (%)")}</label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={localSettings.tax_rate || 10}
            onChange={(e) => handleSettingChange('tax_rate', parseFloat(e.target.value))}
            className="form-input"
            placeholder="10"
          />
        </div>
        <div>
          <label className="form-label">{t("Service Charge (%)")}</label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={localSettings.service_charge || 5}
            onChange={(e) => handleSettingChange('service_charge', parseFloat(e.target.value))}
            className="form-input"
            placeholder="5"
          />
        </div>
      </div>

      {/* Contact Information Section */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("Contact Information")}</h3>
        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="form-label">{t("Business Address")}</label>
            <textarea
              value={localSettings.restaurant_address || ''}
              onChange={(e) => handleSettingChange('restaurant_address', e.target.value)}
              className="form-input"
              rows={2}
              placeholder={t("Enter your business address")}
            />
            <p className="text-sm text-gray-500 mt-1">{t("This address will appear on invoices and receipts")}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">{t("Phone Number")}</label>
              <input
                type="text"
                value={localSettings.restaurant_phone || ''}
                onChange={(e) => handleSettingChange('restaurant_phone', e.target.value)}
                className="form-input"
                placeholder="+1 234 567 8900"
              />
              <p className="text-sm text-gray-500 mt-1">{t("Your business's contact number")}</p>
            </div>
            
            <div>
              <label className="form-label">{t("Email Address")}</label>
              <input
                type="email"
                value={localSettings.restaurant_email || ''}
                onChange={(e) => handleSettingChange('restaurant_email', e.target.value)}
                className="form-input"
                placeholder={t("info@yourbusiness.com")}
              />
              <p className="text-sm text-gray-500 mt-1">{t("Your business's email address")}</p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="form-label">{t("Welcome Message")}</label>
        <textarea
          value={localSettings.welcome_message || ''}
          onChange={(e) => handleSettingChange('welcome_message', e.target.value)}
          className="form-input"
          rows={3}
          placeholder={t("Welcome!")}
        />
      </div>
    </div>
  </div>
        )}

        {/* Branding Settings */}
        {activeTab === 'branding' && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">{t("Branding")}</h2>
              <p className="text-gray-600">{t("Customize your business's appearance")}</p>
            </div>
            <div className="card-body space-y-6">
              <LogoUploadField value={localSettings.logo_url || ''} onChange={(url) => handleSettingChange('logo_url', url)} label="Logo / profile picture" />

              <div>
                <label className="form-label">{t("Favicon URL")}</label>
                <input
                  type="url"
                  value={localSettings.favicon_url || ''}
                  onChange={(e) => handleSettingChange('favicon_url', e.target.value)}
                  className="form-input"
                  placeholder={t("https://example.com/favicon.ico")}
                />
                <p className="text-sm text-gray-500 mt-1">{t("Enter the URL of your favicon")}</p>
              </div>


            </div>
          </div>
        )}
        {activeTab === 'branding' && (
          <SocialLinksFields
            value={localSettings.social_links || {}}
            onChange={(links) => handleSettingChange('social_links', links)}
          />
        )}

        {/* Theme Settings */}
        {activeTab === 'theme' && (
          <BrandColorsPanel
            values={localSettings}
            onChange={handleSettingChange}
            onSave={handleSaveSettings}
            saving={saving}
            websiteUrl={branch?.website_url}
          />
        )}

        {/* Layout Settings */}
        {activeTab === 'layout' && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">{t("Layout Settings")}</h2>
              <p className="text-gray-600">{t("Customize the application layout")}</p>
            </div>
            <div className="card-body space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">{t("Border Radius (px)")}</label>
                  <input
                    type="number"
                    min="0"
                    max="24"
                    value={localSettings.border_radius || 12}
                    onChange={(e) => handleSettingChange('border_radius', parseInt(e.target.value))}
                    className="form-input"
                    placeholder="12"
                  />
                </div>
                <div>
                  <label className="form-label">{t("Sidebar Width (px)")}</label>
                  <input
                    type="number"
                    min="200"
                    max="400"
                    value={localSettings.sidebar_width || 256}
                    onChange={(e) => handleSettingChange('sidebar_width', parseInt(e.target.value))}
                    className="form-input"
                    placeholder="256"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">{t("Shadow Intensity")}</label>
                <select
                  value={localSettings.shadow_intensity || 'medium'}
                  onChange={(e) => handleSettingChange('shadow_intensity', e.target.value)}
                  className="form-input"
                >
                  <option value="light">{t("Light")}</option>
                  <option value="medium">{t("Medium")}</option>
                  <option value="heavy">{t("Heavy")}</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Database Settings */}
        {activeTab === 'database' && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">{t("Database Settings")}</h2>
              <p className="text-gray-600">{t("Configure and manage your database connection")}</p>
            </div>
            <div className="card-body space-y-6">
              {/* Information Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CircleStackIcon className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ms-3">
                    <h3 className="text-sm font-medium text-blue-800">{t("Database Management")}</h3>
                    <div className="mt-2 text-sm text-blue-700 space-y-1">
                      <p>• <strong>{t("Local Mode:")}</strong> {t("Uses SQLite - perfect for single-device setup")}</p>
                      <p>• <strong>{t("Cloud Mode:")}</strong> {t("Uses MySQL - for multi-device/production environments")}</p>
                      <p>• <strong>{t("Initialize:")}</strong> {t("Creates database, tables, and admin user automatically")}</p>
                      <p>• <strong>{t("Export:")}</strong> {t("Download a backup of your entire database")}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Operating Mode Toggle */}
              <div>
                <label className="form-label">{t("Operating Mode")}</label>
                <div className="flex space-x-4 mt-2">
                  <button
                    onClick={() => switchOperatingMode('LOCAL')}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all duration-200 ${
                      localSettings.operatingMode === 'LOCAL'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <ComputerDesktopIcon className="h-8 w-8 mx-auto mb-2" />
                    <h3 className="font-semibold">{t("Local Mode")}</h3>
                    <p className="text-sm text-gray-600">{t("SQLite Database")}</p>
                  </button>
                  <button
                    onClick={() => switchOperatingMode('CLOUD')}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all duration-200 ${
                      localSettings.operatingMode === 'CLOUD'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <CloudIcon className="h-8 w-8 mx-auto mb-2" />
                    <h3 className="font-semibold">{t("Cloud Mode")}</h3>
                    <p className="text-sm text-gray-600">{t("MySQL Database")}</p>
                  </button>
                </div>
              </div>

              {/* Database Configuration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">{t("Database Type")}</label>
                  <select
                    value={localSettings.dbType || 'sqlite3'}
                    onChange={(e) => handleSettingChange('dbType', e.target.value)}
                    className="form-input"
                  >
                    <option value="sqlite3">{t("SQLite")}</option>
                    <option value="mysql2">{t("MySQL")}</option>
                    <option value="pg">{t("PostgreSQL")}</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">{t("Database Host")}</label>
                  <input
                    type="text"
                    value={localSettings.dbHost || 'localhost'}
                    onChange={(e) => handleSettingChange('dbHost', e.target.value)}
                    className="form-input"
                    placeholder={t("localhost")}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">{t("Database Port")}</label>
                  <input
                    type="number"
                    value={localSettings.dbPort || 3306}
                    onChange={(e) => handleSettingChange('dbPort', parseInt(e.target.value))}
                    className="form-input"
                    placeholder="3306"
                  />
                </div>
                <div>
                  <label className="form-label">{t("Database Name")}</label>
                  <input
                    type="text"
                    value={localSettings.dbName || 'posq'}
                    onChange={(e) => handleSettingChange('dbName', e.target.value)}
                    className="form-input"
                    placeholder={t("posq")}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">{t("Database User")}</label>
                  <input
                    type="text"
                    value={localSettings.dbUser || 'posq'}
                    onChange={(e) => handleSettingChange('dbUser', e.target.value)}
                    className="form-input"
                    placeholder={t("posq")}
                  />
                </div>
                <div>
                  <label className="form-label">{t("Database Password")}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={localSettings.dbPassword || 'posqpassword'}
                      onChange={(e) => handleSettingChange('dbPassword', e.target.value)}
                      className="form-input pe-10"
                      placeholder={t("posqpassword")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 end-0 pe-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Show Database Path only in LOCAL mode */}
              {localSettings.operatingMode === 'LOCAL' && (
                <div>
                  <label className="form-label">{t("Database Path (SQLite only)")}</label>
                  <input
                    type="text"
                    value={localSettings.dbPath || './data/posq.db'}
                    onChange={(e) => handleSettingChange('dbPath', e.target.value)}
                    className="form-input"
                    placeholder={t("./data/posq.db")}
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={testDatabaseConnection}
                  disabled={saving}
                  className="btn-outline"
                >
                  {saving ? (
                    <>
                      <div className="loading-spinner me-2"></div>
                      {t("Testing...")}
                    </>
                  ) : (
                    <>
                      <CircleStackIcon className="h-5 w-5 me-2" />
                      {t("Test Connection")}
                    </>
                  )}
                </button>
                
                <button
                  onClick={initializeDatabase}
                  disabled={saving}
                  className="btn-primary"
                >
                  {saving ? (
                    <>
                      <div className="loading-spinner me-2"></div>
                      {t("Initializing...")}
                    </>
                  ) : (
                    <>
                      <CircleStackIcon className="h-5 w-5 me-2" />
                      {t("Initialize Database")}
                    </>
                  )}
                </button>
                
                <button
                  onClick={exportDatabase}
                  disabled={saving}
                  className="btn-outline"
                >
                  {saving ? (
                    <>
                      <div className="loading-spinner me-2"></div>
                      {t("Exporting...")}
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon className="h-5 w-5 me-2" />
                      {t("Export Database")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* UI Text Settings */}
        {activeTab === 'ui_text' && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">{t("UI Text Settings")}</h2>
              <p className="text-gray-600">{t("Customize text displayed to customers")}</p>
            </div>
            <div className="card-body space-y-6">
              <div>
                <label className="form-label">{t("Header Text")}</label>
                <input
                  type="text"
                  value={localSettings.header_text || ''}
                  onChange={(e) => handleSettingChange('header_text', e.target.value)}
                  className="form-input"
                  placeholder={t("Welcome!")}
                />
                <p className="text-sm text-gray-500 mt-1">{t("Text displayed at the top of the menu page")}</p>
              </div>
              
              <div>
                <label className="form-label">{t("Footer Text")}</label>
                <input
                  type="text"
                  value={localSettings.footer_text || ''}
                  onChange={(e) => handleSettingChange('footer_text', e.target.value)}
                  className="form-input"
                  placeholder={t("Thank you for choosing us!")}
                />
                <p className="text-sm text-gray-500 mt-1">{t("Text displayed at the bottom of the menu page")}</p>
              </div>

              <div>
                <label className="form-label">{t("Order Instructions")}</label>
                <input
                  type="text"
                  value={localSettings.order_instructions || ''}
                  onChange={(e) => handleSettingChange('order_instructions', e.target.value)}
                  className="form-input"
                  placeholder={t("Scan QR code to order • Pay at cashier")}
                />
                <p className="text-sm text-gray-500 mt-1">{t("Instructions shown to customers")}</p>
              </div>
            </div>
          </div>
        )}

        {/* Payment Settings */}
        {activeTab === 'payment' && <PaymentMethodsPanel />}

        {/* Backup Settings */}
        {activeTab === 'backup' && (
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{t("Backup & Restore")}</h2>
                  <p className="text-gray-600">{t("Create and manage database backups")}</p>
                </div>
                <button
                  onClick={createBackup}
                  disabled={creatingBackup}
                  className="btn-primary flex items-center space-x-2"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  <span>{creatingBackup ? t("Creating...") : t("Create Backup")}</span>
                </button>
              </div>
            </div>
            <div className="card-body">
              {loadingBackups ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="loading-spinner mb-4"></div>
                    <p className="text-gray-600">{t("Loading backups...")}</p>
                  </div>
                </div>
              ) : backups.length === 0 ? (
                <div className="text-center py-12">
                  <ArrowDownTrayIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t("No backups available")}</h3>
                  <p className="text-gray-600 mb-4">{t("Create your first backup to get started")}</p>
                  <button
                    onClick={createBackup}
                    disabled={creatingBackup}
                    className="btn-primary"
                  >
                    {creatingBackup ? t("Creating...") : t("Create Backup")}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      {t("Available Backups (")}{backups.length})
                    </h3>
                    <p className="text-sm text-gray-500">
                      {t("Click restore to revert your database to a previous state.")} 
                      <span className="text-red-600 font-medium"> {t("Warning: This will replace all current data!")}</span>
                    </p>
                  </div>
                  
                  {backups.map((backup) => (
                    <div
                      key={backup.filename}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{backup.filename}</h4>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                          <span className="flex items-center">
                            <span className="font-medium me-1">{t("Type:")}</span>
                            <span className="capitalize">{backup.type}</span>
                          </span>
                          <span className="flex items-center">
                            <span className="font-medium me-1">{t("Size:")}</span>
                            {(backup.size / 1024).toFixed(2)} {t("KB")}
                          </span>
                          <span className="flex items-center">
                            <span className="font-medium me-1">{t("Created:")}</span>
                            {new Date(backup.created).toLocaleString(locale())}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ms-4">
                        <button
                          onClick={() => restoreBackup(backup.filename)}
                          disabled={saving}
                          className="btn-secondary flex items-center space-x-1 text-sm"
                          title={t("Restore this backup")}
                        >
                          <ArrowUpTrayIcon className="h-4 w-4" />
                          <span>{t("Restore")}</span>
                        </button>
                        <button
                          onClick={() => deleteBackup(backup.filename)}
                          className="btn-danger flex items-center space-x-1 text-sm"
                          title={t("Delete this backup")}
                        >
                          <TrashIcon className="h-4 w-4" />
                          <span>{t("Delete")}</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                  <span className="inline-block w-2 h-2 bg-blue-500 rounded-full me-2"></span>
                  {t("Backup Information")}
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>{t("• Backups include all database tables, settings, and configurations")}</li>
                  <li>{t("• Regular backups help protect against data loss")}</li>
                  <li>{t("• Restore operations will replace ALL current data")}</li>
                  <li>{t("• Keep multiple backups for different restore points")}</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Menu Templates */}
        {activeTab === 'menu_templates' && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">{t("Menu Templates")}</h2>
              <p className="text-gray-600">{t("Choose the perfect layout for your restaurant menu")}</p>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Default Template */}
                <div 
                  onClick={() => handleSettingChange('menu_template', 'default')}
                  className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
                    localSettings.menu_template === 'default' 
                      ? 'border-blue-600 bg-blue-50 shadow-lg' 
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{t("Default - Luxury Gradient")}</h3>
                      <p className="text-sm text-gray-600 mt-1">{t("Modern cards with gradient accents")}</p>
                    </div>
                    {localSettings.menu_template === 'default' && (
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="space-y-2">
                      <div className="h-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full w-1/2"></div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="aspect-square bg-gray-100 rounded"></div>
                        <div className="aspect-square bg-gray-100 rounded"></div>
                        <div className="aspect-square bg-gray-100 rounded"></div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-xs text-gray-500">
                    <span className="inline-block w-2 h-2 bg-blue-500 rounded-full me-2"></span>
                    {t("3-column grid • Gradient hero • Card layout")}
                  </div>
                </div>

                {/* Modern Template */}
                <div 
                  onClick={() => handleSettingChange('menu_template', 'modern')}
                  className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
                    localSettings.menu_template === 'modern' 
                      ? 'border-blue-600 bg-blue-50 shadow-lg' 
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{t("Modern - App Style")}</h3>
                      <p className="text-sm text-gray-600 mt-1">{t("Sticky category sidebar that follows your scroll, wide photo cards")}</p>
                    </div>
                    {localSettings.menu_template === 'modern' && (
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-8 bg-blue-600"></div>
                        <div className="h-3 bg-gray-900 rounded w-1/3"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <div className="w-16 h-16 bg-gray-100 rounded-lg"></div>
                          <div className="flex-1 space-y-1">
                            <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                            <div className="h-2 bg-gray-100 rounded w-full"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-xs text-gray-500">
                    <span className="inline-block w-2 h-2 bg-gray-900 rounded-full me-2"></span>
                    {t("2-column list • Large images • Bold headers")}
                  </div>
                </div>

                {/* Elegant Template */}
                <div 
                  onClick={() => handleSettingChange('menu_template', 'elegant')}
                  className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
                    localSettings.menu_template === 'elegant' 
                      ? 'border-blue-600 bg-blue-50 shadow-lg' 
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{t("Elegant - Printed Menu")}</h3>
                      <p className="text-sm text-gray-600 mt-1">{t("A cream paper carte in two columns with dotted price leaders, no photos")}</p>
                    </div>
                    {localSettings.menu_template === 'elegant' && (
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="space-y-2">
                      <div className="h-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded w-1/3 mx-auto"></div>
                      <div className="flex gap-2">
                        <div className="w-24 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded"></div>
                        <div className="flex-1 space-y-1">
                          <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-2 bg-gray-100 rounded w-full"></div>
                          <div className="h-2 bg-gray-100 rounded w-5/6"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-xs text-gray-500">
                    <span className="inline-block w-2 h-2 bg-amber-500 rounded-full me-2"></span>
                    {t("Full-width list • Serif fonts • Luxury feel")}
                  </div>
                </div>

                {/* Minimal Template */}
                <div 
                  onClick={() => handleSettingChange('menu_template', 'minimal')}
                  className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
                    localSettings.menu_template === 'minimal' 
                      ? 'border-blue-600 bg-blue-50 shadow-lg' 
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{t("Minimal - Swiss Tabs")}</h3>
                      <p className="text-sm text-gray-600 mt-1">{t("Black on white, one category at a time behind tabs")}</p>
                    </div>
                    {localSettings.menu_template === 'minimal' && (
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="space-y-2">
                      <div className="h-2 bg-gray-900 rounded w-1/4"></div>
                      <div className="grid grid-cols-4 gap-1">
                        <div className="aspect-square bg-gray-50 border border-gray-200 rounded"></div>
                        <div className="aspect-square bg-gray-50 border border-gray-200 rounded"></div>
                        <div className="aspect-square bg-gray-50 border border-gray-200 rounded"></div>
                        <div className="aspect-square bg-gray-50 border border-gray-200 rounded"></div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-xs text-gray-500">
                    <span className="inline-block w-2 h-2 bg-gray-900 rounded-full me-2"></span>
                    {t("4-column grid • Minimal borders • Light typography")}
                  </div>
                </div>

                {/* Luxury Template */}
                <div
                  onClick={() => handleSettingChange('menu_template', 'luxury')}
                  className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
                    localSettings.menu_template === 'luxury'
                      ? 'border-blue-600 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{t("Luxury - Fine Dining")}</h3>
                      <p className="text-sm text-gray-600 mt-1">{t("Black canvas, gold accents, serif type")}</p>
                    </div>
                    {localSettings.menu_template === 'luxury' && (
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="bg-[#0b0b0c] rounded-lg p-4 border border-gray-800">
                    <div className="space-y-3">
                      <div className="h-2 bg-[#c9a961] rounded w-1/3 mx-auto"></div>
                      <div className="space-y-2 pt-1">
                        <div className="flex justify-between border-b border-white/10 pb-2">
                          <div className="h-2 bg-white/70 rounded w-1/2"></div>
                          <div className="h-2 bg-[#c9a961] rounded w-8"></div>
                        </div>
                        <div className="flex justify-between border-b border-white/10 pb-2">
                          <div className="h-2 bg-white/70 rounded w-2/5"></div>
                          <div className="h-2 bg-[#c9a961] rounded w-8"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-xs text-gray-500">
                    <span className="inline-block w-2 h-2 bg-[#c9a961] rounded-full me-2"></span>
                    {t("Dark theme • Serif headings • Quiet pricing")}
                  </div>
                </div>

                {/* Healthy Template */}
                <div
                  onClick={() => handleSettingChange('menu_template', 'healthy')}
                  className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
                    localSettings.menu_template === 'healthy'
                      ? 'border-blue-600 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{t("Healthy - Diet & Wellness")}</h3>
                      <p className="text-sm text-gray-600 mt-1">{t("Fresh cards with dietary tag badges")}</p>
                    </div>
                    {localSettings.menu_template === 'healthy' && (
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="space-y-2">
                      <div className="h-3 bg-emerald-100 rounded-full w-1/2 mx-auto"></div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="aspect-square bg-emerald-50 border border-emerald-100 rounded relative">
                          <div className="absolute top-1 start-1 h-1.5 w-4 bg-emerald-300 rounded-full"></div>
                        </div>
                        <div className="aspect-square bg-emerald-50 border border-emerald-100 rounded"></div>
                        <div className="aspect-square bg-emerald-50 border border-emerald-100 rounded"></div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-xs text-gray-500">
                    <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full me-2"></span>
                    {t("Auto dietary tags • Clean grid • Fresh palette")}
                  </div>
                </div>

                {/* Casual Template */}
                <div
                  onClick={() => handleSettingChange('menu_template', 'casual')}
                  className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
                    localSettings.menu_template === 'casual'
                      ? 'border-blue-600 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{t("Casual - Fast & Bold")}</h3>
                      <p className="text-sm text-gray-600 mt-1">{t("Bright colors, big one-tap add buttons")}</p>
                    </div>
                    {localSettings.menu_template === 'casual' && (
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="space-y-2">
                      <div className="h-4 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg w-2/3 mx-auto"></div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="aspect-square bg-orange-50 border-2 border-orange-200 rounded-lg"></div>
                        <div className="aspect-square bg-orange-50 border-2 border-orange-200 rounded-lg"></div>
                        <div className="aspect-square bg-orange-50 border-2 border-orange-200 rounded-lg"></div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-xs text-gray-500">
                    <span className="inline-block w-2 h-2 bg-orange-500 rounded-full me-2"></span>
                    {t("2-3 column grid • High contrast • Quick ordering")}
                  </div>
                </div>

                {/* Cafe Template */}
                <div
                  onClick={() => handleSettingChange('menu_template', 'cafe')}
                  className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
                    localSettings.menu_template === 'cafe'
                      ? 'border-blue-600 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{t("Cafe - Chalkboard")}</h3>
                      <p className="text-sm text-gray-600 mt-1">{t("Handwritten chalk menu in a wooden frame, round photos")}</p>
                    </div>
                    {localSettings.menu_template === 'cafe' && (
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="bg-[#fbf4e9] rounded-lg p-4 border border-[#e8dcc8]">
                    <div className="space-y-2">
                      <div className="h-3 bg-[#a8672f]/30 rounded w-1/3 mx-auto"></div>
                      <div className="flex gap-2 bg-white rounded-lg p-2">
                        <div className="w-10 h-10 bg-[#a8672f]/20 rounded-lg flex-shrink-0"></div>
                        <div className="flex-1 space-y-1 pt-1">
                          <div className="h-1.5 bg-[#6b4a34]/30 rounded w-2/3"></div>
                          <div className="h-1.5 bg-[#6b4a34]/15 rounded w-1/2"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-xs text-gray-500">
                    <span className="inline-block w-2 h-2 bg-[#a8672f] rounded-full me-2"></span>
                    {t("Rounded photos • Soft serif • Cozy palette")}
                  </div>
                </div>

                {/* Steakhouse Template */}
                <div
                  onClick={() => handleSettingChange('menu_template', 'steakhouse')}
                  className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
                    localSettings.menu_template === 'steakhouse'
                      ? 'border-blue-600 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{t("Steakhouse - Grill & BBQ")}</h3>
                      <p className="text-sm text-gray-600 mt-1">{t("Full-width photo banner for every section, heavy slab type")}</p>
                    </div>
                    {localSettings.menu_template === 'steakhouse' && (
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="bg-[#1c1410] rounded-lg p-4 border border-[#3d2f24]">
                    <div className="space-y-2">
                      <div className="h-3 bg-[#b5482a] rounded w-1/3 mx-auto"></div>
                      <div className="flex gap-2 bg-[#2a2019] rounded-lg p-2">
                        <div className="w-10 h-10 bg-[#3d2f24] rounded flex-shrink-0"></div>
                        <div className="flex-1 space-y-1 pt-1">
                          <div className="h-1.5 bg-[#f4ebe0]/40 rounded w-2/3"></div>
                          <div className="h-1.5 bg-[#e8834f]/60 rounded w-1/3"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-xs text-gray-500">
                    <span className="inline-block w-2 h-2 bg-[#b5482a] rounded-full me-2"></span>
                    {t("Dark wood palette • Bold slab type • Rustic")}
                  </div>
                </div>

                {/* Sushi Template */}
                <div
                  onClick={() => handleSettingChange('menu_template', 'sushi')}
                  className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
                    localSettings.menu_template === 'sushi'
                      ? 'border-blue-600 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{t("Sushi - Japanese Minimal")}</h3>
                      <p className="text-sm text-gray-600 mt-1">{t("Each category a sliding counter of tall numbered cards")}</p>
                    </div>
                    {localSettings.menu_template === 'sushi' && (
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="bg-[#faf8f5] rounded-lg p-4 border border-[#e5e0d8]">
                    <div className="space-y-2">
                      <div className="h-2 w-2 bg-[#c23b3b] rounded-full mx-auto"></div>
                      <div className="flex gap-2 items-center">
                        <div className="w-8 h-8 bg-[#f0ece4] rounded-full flex-shrink-0"></div>
                        <div className="flex-1 space-y-1">
                          <div className="h-1.5 bg-[#2b2b2b]/30 rounded w-2/3"></div>
                          <div className="h-1.5 bg-[#9a9a9a]/30 rounded w-1/2"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-xs text-gray-500">
                    <span className="inline-block w-2 h-2 bg-[#c23b3b] rounded-full me-2"></span>
                    {t("Thin typography • Whitespace • One red accent")}
                  </div>
                </div>

                {/* Bar Template */}
                <div
                  onClick={() => handleSettingChange('menu_template', 'bar')}
                  className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
                    localSettings.menu_template === 'bar'
                      ? 'border-blue-600 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{t("Bar - Nightlife")}</h3>
                      <p className="text-sm text-gray-600 mt-1">{t("Neon drinks board: pick a category, see only that list")}</p>
                    </div>
                    {localSettings.menu_template === 'bar' && (
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="bg-[#0f0a17] rounded-lg p-4 border border-white/10">
                    <div className="space-y-2">
                      <div className="h-3 bg-gradient-to-r from-[#ff2fb0] via-[#a855f7] to-[#22d3ee] rounded w-1/2 mx-auto"></div>
                      <div className="flex gap-2 bg-white/5 rounded-lg p-2">
                        <div className="w-10 h-10 bg-white/10 rounded flex-shrink-0"></div>
                        <div className="flex-1 space-y-1 pt-1">
                          <div className="h-1.5 bg-white/40 rounded w-2/3"></div>
                          <div className="h-1.5 bg-[#22d3ee]/60 rounded w-1/3"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-xs text-gray-500">
                    <span className="inline-block w-2 h-2 bg-[#a855f7] rounded-full me-2"></span>
                    {t("Neon gradient • Bold rounded type • Moody")}
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                  <PaintBrushIcon className="w-5 h-5 me-2" />
                  {t("Template Preview")}
                </h4>
                <p className="text-sm text-blue-800">
                  {t("Choose the template that best matches your restaurant's style. The selected template will be used for all customer-facing menu pages accessed via QR codes.")}
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'store_templates' && (
          <TemplatePicker
            title={t("Store Templates")}
            description={t("Choose the layout for your product catalog")}
            templates={STORE_TEMPLATES}
            value={localSettings.store_template || 'default'}
            onChange={(v) => handleSettingChange('store_template', v)}
          />
        )}

        {activeTab === 'theme_upload' && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">{t("Upload a storefront theme")}</h2>
              <p className="text-gray-600">{t("Upload one validated theme package and use it for your restaurant, shop, or appointment website.")}</p>
            </div>
            <div className="card-body space-y-6">
              <div className="rounded-xl bg-slate-900 p-5 text-sm text-slate-100 overflow-auto">
                <p className="text-slate-400 mb-2">{t("Example theme.json")}</p>
                <pre>{t("{\n  \"name\": \"Olive Sunday\",\n  \"businessType\": \"restaurant\",\n  \"template\": \"modern\",\n  \"colors\": { \"primary\": \"#263f32\", \"accent\": \"#c48a3a\" },\n  \"heroTitle\": \"A table worth gathering around.\",\n  \"heroSubtitle\": \"Seasonal plates, warm service.\"\n}")}</pre>
              </div>
              <label className="block border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center cursor-pointer hover:border-blue-500 transition">
                <ArrowUpTrayIcon className="h-8 w-8 mx-auto text-slate-400 mb-3" />
                <span className="font-medium text-slate-800">{themeFile?.name || t("Choose a theme.json file")}</span>
                <span className="block text-sm text-slate-500 mt-2">{t("JSON theme packages up to 1 MB")}</span>
                <input className="sr-only" type="file" accept=".json,.theme,application/json" onChange={event => setThemeFile(event.target.files?.[0] || null)} />
              </label>
              <button type="button" className="btn-primary" disabled={!themeFile || themeBusy} onClick={uploadTheme}>{themeBusy ? t("Saving theme…") : t("Upload and save theme")}</button>
              {uploadedTheme && <div className="rounded-2xl border border-slate-200 p-5 flex flex-wrap items-center justify-between gap-4">
                <div><p className="font-semibold text-slate-900">{uploadedTheme.name}</p><p className="text-sm text-slate-500">{uploadedTheme.businessType} · {uploadedTheme.template} · v{uploadedTheme.version}</p></div>
                <div className="flex gap-2"><button type="button" className="btn-primary" disabled={themeBusy} onClick={() => activateTheme(uploadedTheme.active === false)}> {uploadedTheme.active === false ? t("Activate theme") : t("Pause theme")} </button><button type="button" className="btn-secondary" disabled={themeBusy} onClick={async () => { await themesAPI.remove(); setUploadedTheme(null); toast.success(t("Uploaded theme removed")) }}>{t("Remove")}</button></div>
              </div>}
              <p className="text-xs text-slate-500">{t("Uploaded files are treated as design data. Scripts and arbitrary HTML are never executed.")}</p>
            </div>
          </div>
        )}

        {activeTab === 'booking_templates' && (
          <TemplatePicker
            title={t("Booking Page")}
            description={t("Choose the layout customers see when booking with you")}
            templates={BOOKING_TEMPLATES}
            value={localSettings.booking_template || 'default'}
            onChange={(v) => handleSettingChange('booking_template', v)}
          />
        )}

        {activeTab === 'billing' && <RestaurantBillingPanel />}

        {/* Printer Settings */}
        {activeTab === 'printer' && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">{t("Printer Settings")}</h2>
              <p className="text-gray-600">{t("Configure thermal printer settings")}</p>
            </div>
            <div className="card-body space-y-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={localSettings.printerEnabled || false}
                  onChange={(e) => handleSettingChange('printerEnabled', e.target.checked)}
                  className="form-checkbox"
                />
                <label className="ms-2 text-sm text-gray-700">{t("Enable Printer")}</label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">{t("Printer IP Address")}</label>
                  <input
                    type="text"
                    value={localSettings.printerIp || '192.168.1.100'}
                    onChange={(e) => handleSettingChange('printerIp', e.target.value)}
                    className="form-input"
                    placeholder="192.168.1.100"
                  />
                </div>
                <div>
                  <label className="form-label">{t("Printer Port")}</label>
                  <input
                    type="number"
                    value={localSettings.printerPort || 9100}
                    onChange={(e) => handleSettingChange('printerPort', parseInt(e.target.value))}
                    className="form-input"
                    placeholder="9100"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SettingsPage
